import React, {useContext, useMemo, useState} from 'react';
import {Button, Form, Input, InputNumber, Select, Tag} from 'antd';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {TrophyChallengeGroupEntity} from "./TrophyChallengeGroupEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import {useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {useEventRewardList} from "./useTrophyChallenge";
import {useItemKrTextData} from "../../probability/useItemData";
import {AuthContext} from "../../../shared/contexts/authContext";

const {Option} = Select;
const MAX_REWARDS = 8;

const TrophyChallengeGroupEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const {currentUser} = useContext(AuthContext);
    const resourceOptions = modalOptions?.resourceOptions || [];
    const {data: eventRewardList} = useEventRewardList();
    const {data: itemRaw} = useItemKrTextData();
    const itemData = useMemo(() => itemRaw?.[currentUser?.target] || [], [itemRaw, currentUser]);

    const [rows, setRows] = useState(
        record && record.seq ? new TrophyChallengeGroupEntity(record) : new TrophyChallengeGroupEntity({})
    );
    const [rewardInput, setRewardInput] = useState(null);

    const getRewardLabel = (index) => {
        const entry = (eventRewardList || []).find(e => e.index === index);
        if (!entry) return '';
        return (entry.rewards || []).map(r => {
            const item = itemData.find(d => d.index === r.id && d.itemType === r.type);
            return item ? `${item.name}×${r.quantity}` : `(t${r.type})${r.id}×${r.quantity}`;
        }).join(', ');
    };

    const handleSetRows = (key, value) => {
        setRows(prev => new TrophyChallengeGroupEntity({...prev, [key]: value}));
    };

    const handleAddReward = () => {
        if (rewardInput == null) {
            toast.error('보상을 선택해주세요.');
            return;
        }
        if (rows.completeReward.includes(rewardInput)) {
            toast.error('이미 추가된 보상입니다.');
            return;
        }
        if (rows.completeReward.length >= MAX_REWARDS) {
            toast.error('더 이상 보상을 등록할 수 없습니다.');
            return;
        }
        handleSetRows('completeReward', [...rows.completeReward, rewardInput]);
        setRewardInput(null);
    };

    const handleRemoveReward = (idx) => {
        handleSetRows('completeReward', rows.completeReward.filter((_, i) => i !== idx));
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (rows.isInvalidate()) return;
        return internalApi.post('trophy_challenge/group', {data: rows.toJson()}).then(res => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['trophyChallengeGroup']});
            queryClient.invalidateQueries({queryKey: ['trophyChallengeResourceList']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch(err => {
            toast.error(err?.response?.data?.message || '저장 실패');
            console.error(err);
        });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl(`trophy_challenge/group/${rows.seq}`)).then(res => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['trophyChallengeGroup']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch(err => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <Form.Item label="메모">
                    <Input
                        value={rows.memo}
                        onChange={e => handleSetRows('memo', e.target.value)}
                        placeholder="관리용 메모"
                    />
                </Form.Item>

                <div style={{display: 'flex', gap: 16}}>
                    <Form.Item label="리소스 번호" required style={{width: 160}}>
                        {resourceOptions.length > 0 ? (
                            <Select
                                value={rows.resourceIdx || undefined}
                                onChange={v => handleSetRows('resourceIdx', v)}
                                placeholder="리소스 선택"
                                showSearch
                                style={{width: '100%'}}
                            >
                                {resourceOptions.map(idx => (
                                    <Option key={idx} value={idx}>{idx}</Option>
                                ))}
                            </Select>
                        ) : (
                            <InputNumber
                                min={0}
                                value={rows.resourceIdx}
                                onChange={v => handleSetRows('resourceIdx', v ?? 0)}
                                style={{width: '100%'}}
                                placeholder="TrophyChallenge_Resource index"
                            />
                        )}
                    </Form.Item>
                    <Form.Item label="최소 적용 버전" required style={{width: 160}}>
                        <Input
                            value={rows.minVersion}
                            onChange={e => handleSetRows('minVersion', e.target.value)}
                            placeholder="예: 1.12.1"
                        />
                    </Form.Item>
                </div>

                <Form.Item label={`최종 보상 (Event_Reward index, 최대 ${MAX_REWARDS}개)`}>
                    <div style={{display: 'flex', gap: 8, marginBottom: 8}}>
                        <Select
                            value={rewardInput}
                            onChange={v => setRewardInput(v)}
                            placeholder="보상 선택"
                            showSearch
                            style={{flex: 1}}
                            allowClear
                            dropdownStyle={{background: 'var(--ant-color-bg-elevated, #fff)', backdropFilter: 'none'}}
                            filterOption={(input, opt) =>
                                String(opt.value).includes(input) ||
                                (opt.searchLabel || '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {(eventRewardList || []).map(entry => {
                                const label = getRewardLabel(entry.index);
                                return (
                                    <Option key={entry.index} value={entry.index} searchLabel={`${entry.index} ${label}`}>
                                        <span style={{fontWeight: 600}}>[{entry.index}]</span>
                                        {label && <span style={{marginLeft: 6, color: '#888', fontSize: 12}}>{label}</span>}
                                    </Option>
                                );
                            })}
                        </Select>
                        <Button
                            onClick={handleAddReward}
                            disabled={rows.completeReward.length >= MAX_REWARDS}
                        >
                            추가하기
                        </Button>
                    </div>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                        {rows.completeReward.map((r, i) => {
                            const label = getRewardLabel(r);
                            return (
                                <Tag key={i} closable onClose={() => handleRemoveReward(i)} color="blue">
                                    {r}{label && <span style={{marginLeft: 4, fontWeight: 400, fontSize: 11}}>{label}</span>}
                                </Tag>
                            );
                        })}
                    </div>
                </Form.Item>

                <div style={{marginTop: 16}}>
                    <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={rows.seq > 0 ? 'flex' : 'none'}
                    />
                </div>
            </Form>
        </div>
    );
};

export default TrophyChallengeGroupEditModal;
